import type { Legislation } from "@/models/legislation/Legislation";
import type { LegislationSummary } from "@/models/legislation/LegislationSummary";

/** A full legislation row, or the list fields of one (which carry the same raw metadata). */
export type LegislationRow = Legislation | LegislationSummary;

/**
 * Definition for a dynamic filter/column that can extract values from metadata
 */
export type DynamicFilterDef = {
  key: string;
  displayName?: string;
  type?: "Multi" | "Single" | "Boolean";
  databaseColumn?: string | null;
  valueMapping?: Record<string, string> | null;
};

/**
 * Parses metadata from a Legislation object.
 * Handles both pre-parsed metadata objects and JSON strings (metadataObject).
 */
export const parseMetadataObject = (
  row: LegislationRow
): Record<string, unknown> | undefined => {
  const rawMetaCandidate: unknown =
    (
      row as unknown as {
        metadataObject?: unknown;
        metadata?: unknown;
        metadata_object?: unknown;
      }
    ).metadataObject ??
    (row as unknown as { metadata?: unknown }).metadata ??
    (row as unknown as { metadata_object?: unknown }).metadata_object;

  if (typeof rawMetaCandidate === "string") {
    try {
      const parsed = JSON.parse(rawMetaCandidate);
      if (parsed && typeof parsed === "object") {
        return parsed as Record<string, unknown>;
      }
    } catch {
      return undefined;
    }
  } else if (rawMetaCandidate && typeof rawMetaCandidate === "object") {
    return rawMetaCandidate as Record<string, unknown>;
  }

  return undefined;
};

/**
 * Resolves a value from a Legislation row using a Postgres-style JSON path
 * (e.g. metadata_object->'authority'->>'long_name') and supports fallback
 * to top-level or metadata keys.
 */
export const resolveValueFromDbColumn = (
  row: LegislationRow,
  def: DynamicFilterDef
): unknown => {
  const metaObj = parseMetadataObject(row);

  // If no databaseColumn provided, fall back to key or metadata[key]
  if (!def.databaseColumn) {
    const rowRecord = row as unknown as Record<string, unknown>;
    const fromTop = rowRecord?.[def.key];
    const fromMeta = metaObj?.[def.key];
    return typeof fromTop !== "undefined" ? fromTop : fromMeta;
  }

  // Parse Postgres JSON traversal like:
  //   metadata_object->>'k'            => keys: ["k"]
  //   metadata_object->'a'->>'b'       => keys: ["a","b"]
  const keys: string[] = [];
  const re = /->>?'([^']+)'/g; // captures each quoted key after -> or ->>
  let m: RegExpExecArray | null;
  while ((m = re.exec(def.databaseColumn)) !== null) {
    keys.push(m[1]);
  }

  if (keys.length > 0 && metaObj) {
    let cur: unknown = metaObj as Record<string, unknown>;
    for (const k of keys) {
      if (
        cur &&
        typeof cur === "object" &&
        k in (cur as Record<string, unknown>)
      ) {
        cur = (cur as Record<string, unknown>)[k];
      } else {
        cur = undefined;
        break;
      }
    }
    if (cur !== undefined) return cur;
  }

  // If regex didn't match (or value missing), try simple top-level column name
  const simpleCol = def.databaseColumn.replace(/"/g, "");
  const rowRecord = row as unknown as Record<string, unknown>;
  if (simpleCol in rowRecord) return rowRecord[simpleCol];

  // Fallbacks
  const top = rowRecord?.[def.key];
  const meta = metaObj?.[def.key];
  return typeof top !== "undefined" ? top : meta;
};

/**
 * Applies value mapping to convert database values to display labels.
 * Handles arrays, booleans, and string values.
 */
export const humanizeWithMapping = (
  value: unknown,
  mapping?: Record<string, string> | null
): unknown => {
  if (!mapping) return value;
  const entries = Object.entries(mapping);
  const keys = Object.keys(mapping);
  const norm = (x: unknown) => String(x).toLowerCase();

  if (Array.isArray(value)) {
    return value.map((v) => humanizeWithMapping(v, mapping));
  }

  if (value === null || typeof value === "undefined") return value;

  // 1) If the incoming value matches a KEY (label) → keep label (display value)
  const asStr = String(value);
  const keyMatch = keys.find((k) => norm(k) === norm(asStr));
  if (keyMatch) return keyMatch; // it's already a human label

  // 2) Else, if the incoming value matches a VALUE (db code) → return its KEY (label)
  const rev = entries.find(([, v]) => norm(v) === norm(asStr));
  if (rev) return rev[0];

  // 3) Booleans: try matching against mapping values "true"/"false"/"1"/"0"
  if (typeof value === "boolean") {
    const boolStr = String(value);
    const revBool = entries.find(([, v]) => norm(v) === norm(boolStr));
    if (revBool) return revBool[0];
  }

  return value; // no mapping
};

/**
 * Renders a value for display in a table cell.
 * Handles arrays, booleans, null/undefined, and objects.
 */
export const renderCellValue = (value: unknown): string => {
  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "boolean") return value ? "Ja" : "Nej";
  if (value === null || typeof value === "undefined") return "-";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};
